/**
 * Deterministic, seedable random number generation, shared verbatim by the web
 * client and the backend so a `(seed, moveList)` pair reproduces a game exactly.
 *
 * Integer-only math (`Math.imul`, `>>> 0`) makes the output identical across any
 * V8/JS runtime (browser, Worker, Node).
 */

/** A source of floats in `[0, 1)`, like `Math.random`. */
export interface Rng {
  next(): number;
}

/**
 * xmur3 string hash → a function producing successive 32-bit seed words.
 * Used to turn a string seed into a numeric one for {@link mulberry32}.
 */
export function xmur3(str: string): () => number {
  let h = 1779033703 ^ str.length;
  for (let i = 0; i < str.length; i++) {
    h = Math.imul(h ^ str.charCodeAt(i), 3432918353);
    h = (h << 13) | (h >>> 19);
  }
  return () => {
    h = Math.imul(h ^ (h >>> 16), 2246822507);
    h = Math.imul(h ^ (h >>> 13), 3266489909);
    h ^= h >>> 16;
    return h >>> 0;
  };
}

/** mulberry32 PRNG → floats in `[0, 1)` from a single 32-bit state word. */
export function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * Build a deterministic {@link Rng} from a numeric or string seed.
 * String seeds are hashed with {@link xmur3} first.
 */
export function seededRng(seed: number | string): Rng {
  const seedWord = typeof seed === "string" ? xmur3(seed)() : seed >>> 0;
  const next = mulberry32(seedWord);
  return { next };
}

/** Non-deterministic RNG backed by `Math.random` — used for casual classic play. */
export const mathRandomRng: Rng = {
  next: () => Math.random(),
};
